<script lang="ts">
  import { generateId, getUiclContext } from "#lib/internal/helpers"
  import type { BaseProps } from "#lib/internal/types"
  import { cn } from "#lib/utils"
  import {
    CheckboxFilled,
    Checkbox as CheckboxIcon,
    CheckboxSelectAll,
  } from "@tastyworks/icons-2.0"
  import type { Snippet } from "svelte"
  import {
    checkboxContainerVariants,
    checkboxInputVariants,
    checkboxLabelVariants,
  } from "./variants"

  type Props = BaseProps<"button"> & {
    checked?: true | false | "indeterminate"
    label?: string | undefined
    labelClass?: string | undefined | null
    alignment?: "left" | "right"
    onchange?: (event: Event) => void
    children?: Snippet
  }

  let {
    class: className = undefined,
    checked = $bindable(),
    disabled = false,
    alignment = "left",
    label = undefined,
    id = generateId(),
    name = undefined,
    labelClass = undefined,
    onchange,
    children,
    ...restProps
  }: Props = $props()

  const v2 = !!getUiclContext()?.v2

  const isIndeterminate = $derived(checked === "indeterminate")
  let isChecked = $derived(checked === true)

  function handleClick(event: Event) {
    checked = isChecked ? false : true
    onchange?.(event)
  }
  function handleChange(event: Event) {
    event.stopPropagation()
  }
</script>

<div
  class={cn(checkboxContainerVariants({ alignment, v2 }), className)}
  data-disabled={disabled}
>
  <div class="relative h-5 w-5">
    <input
      type="checkbox"
      checked={isChecked}
      {id}
      {name}
      aria-checked={isIndeterminate ? "mixed" : isChecked}
      aria-label={label}
      {disabled}
      onclick={handleClick}
      onchange={handleChange}
      class={cn(checkboxInputVariants({ v2 }))}
      {...restProps as any}
    />
    <div class="pointer-events-none absolute">
      {#if isChecked}
        <CheckboxFilled
          class={cn("h-5 w-5", v2 && disabled && "text-icon-general-disabled")}
        />
      {:else if isIndeterminate}
        <CheckboxSelectAll
          class={cn("h-5 w-5", v2 && disabled && "text-icon-general-disabled")}
        />
      {:else}
        <CheckboxIcon
          class={cn("h-5 w-5", v2 && disabled && "text-icon-general-disabled")}
        />
      {/if}
    </div>
  </div>

  {#if label}
    <label
      for={id}
      class={cn(
        checkboxLabelVariants({ v2 }),
        labelClass,
        v2 && disabled && "text-icon-general-disabled",
        isChecked ? "text-general-primary-text" : "text-general-secondary-text"
      )}
    >
      {label}
    </label>
  {:else}
    {@render children?.()}
  {/if}
</div>
